import { Injectable } from '@angular/core';
import { IVideoVendorService } from '../../../models/util/videoVendor';
import { Subject, Subscription } from 'rxjs';
import { IUploadTrackerConfig } from '../../../models/util/uploadFileTrackerConfig';
import { environment } from '../../../../environments/environment';
import { isValidNumber, isValidString } from '@pineapplelab/util';
import { ApiService } from '../../api/api.service';
import * as CryptoJS from 'crypto-js';

@Injectable({
	providedIn: 'root',
})
export class AwsService implements IVideoVendorService {
	_cancelUpload$ = new Subject<void>();

	constructor(private api: ApiService) {}

	uploadFileWithListener(config: IUploadTrackerConfig): Subscription {
		const taskSub = new Subscription();
		const relPath = this.getRelativePath(config.path);
		const videoId = this.getVideoId(relPath);

		this.startUploadProcess(videoId, relPath, config, taskSub);
		return taskSub;
	}

	private async startUploadProcess(
		videoId: string,
		relPath: string,
		config: IUploadTrackerConfig,
		taskSub: Subscription
	): Promise<void> {
		try {
			const contentMd5 = await this.getContentMd5(config.file);
			const signedUrl = await this.getSignedUploadUrl(videoId, contentMd5);
			this.uploadFile(signedUrl, relPath, videoId, contentMd5, config, taskSub);
		} catch (err) {
			await config.onError?.(err);
		}
	}

	private async getSignedUploadUrl(videoId: string, contentMd5: string): Promise<string> {
		const encodedContentMd5 = encodeURIComponent(contentMd5);
		const data = (await this.api.get(`/s3/video/upload-url?id=${videoId}&contentMd5=${encodedContentMd5}`)) as any;
		return data.url as string;
	}

	private async getContentMd5(file: File): Promise<string> {
		const arrayBuffer = await file.arrayBuffer();
		const wordArray = CryptoJS.lib.WordArray.create(arrayBuffer as any);
		return CryptoJS.MD5(wordArray).toString(CryptoJS.enc.Base64);
	}

	private uploadFile(
		signedUrl: string,
		relPath: string,
		videoId: string,
		contentMd5: string,
		config: IUploadTrackerConfig,
		taskSub: Subscription
	): void {
		const xhr = new XMLHttpRequest();
		const cancelSub = this._cancelUpload$.subscribe(() => xhr.abort());
		taskSub.add(cancelSub);

		xhr.upload.addEventListener('progress', (e: ProgressEvent) => {
			if (e.lengthComputable) {
				const progress = Math.round((e.loaded / e.total) * 100);
				if (isValidNumber(progress) && progress < 100) {
					config.onProgress(progress);
				}
			}
		});

		xhr.addEventListener('load', async () => {
			if (xhr.status >= 200 && xhr.status < 300) {
				const publicUrl = `https://${environment.bucket.originalName}.s3.${environment.bucket.config.region}.amazonaws.com/${relPath}`;
				await config.onSuccess(publicUrl);
				return;
			} else {
				await config.onError?.(xhr);
			}
			cancelSub.unsubscribe();
		});

		xhr.addEventListener('error', async (err) => {
			await config.onError?.(err);
			cancelSub.unsubscribe();
		});

		xhr.open('PUT', signedUrl, true);
		xhr.setRequestHeader('Content-Type', config.file.type);
		xhr.setRequestHeader('Content-MD5', contentMd5);
		config.onStarted?.();
		xhr.send(config.file);
	}

	private getVideoId(relPath: string): string {
		return relPath.split('/')[0];
	}

	getRelativePath(fullPath: string): string {
		if (!isValidString(fullPath)) {
			throw new Error('invalid path');
		}

		const parts = fullPath.split('/');
		return parts.slice(-2).join('/');
	}

	async transcode(videoId: string): Promise<void> {
		if (!videoId) {
			return;
		}
		const path = `${videoId}/video.mp4`;

		const body = {
			inputKey: path,
		};
		this.api.post('/transcoding/transcode', body, { showAPIError: false });
	}
}
